import { useEffect, useRef } from 'react';

const useTrackingScripts = (scriptsRawCode: string | string[]) => {
  // Create a set to track added scripts and noscripts to make sure they're not added
  // multiple times
  const injectedElements = useRef(new Set<string>());
  // Tracking scripts added by the tracking scripts themselves
  const dynamicallyAddedScripts = useRef(new Set<HTMLScriptElement>());
  const observerRef = useRef<MutationObserver | null>(null);

  const parsedCode = Array.isArray(scriptsRawCode) ? scriptsRawCode.join('\n') : scriptsRawCode;

  useEffect(() => {
    // Create a MutationObserver to track dynamically added scripts
    observerRef.current = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeName === 'SCRIPT') {
              const script = node as HTMLScriptElement;
              if (script.src) {
                if (!injectedElements.current.has(script.src)) {
                  injectedElements.current.add(script.src);
                  dynamicallyAddedScripts.current.add(script);
                }
              } else {
                if (!injectedElements.current.has(script.innerHTML)) {
                  injectedElements.current.add(script.innerHTML);
                  dynamicallyAddedScripts.current.add(script);
                }
              }
            }
          });
        }
      });
    });

    // Start observing the HEAD for added scripts
    observerRef.current.observe(document.head, { childList: true, subtree: true });

    const fragment = document.createRange().createContextualFragment(parsedCode);

    // Separate scripts and noscripts
    const scripts = Array.from(fragment.querySelectorAll('script'));
    const noscripts = Array.from(fragment.querySelectorAll('noscript'));

    // Append and track elements
    const appendAndTrackElement = (element: HTMLElement, parent: HTMLElement) => {
      if (!injectedElements.current.has(element.innerHTML)) {
        parent.appendChild(element);
        injectedElements.current.add(element.innerHTML);
      }
    };

    // Append scripts to the head
    const appendedScripts = scripts.map(script => {
      const newScript = document.createElement('script');
      if (script.src) {
        newScript.src = script.src;
      } else {
        newScript.innerHTML = script.innerHTML;
      }
      appendAndTrackElement(newScript, document.head);
      return newScript;
    });

    // Append noscripts to the body
    const appendedNoscripts = noscripts.map(noscript => {
      const newNoscript = document.createElement('noscript');
      newNoscript.innerHTML = noscript.innerHTML;
      appendAndTrackElement(newNoscript, document.body);
      return newNoscript;
    });

    return () => {
      // Cleanup, remove the tracking scripts when the component unmounts as they're not
      // needed anymore
      appendedScripts.forEach(script => {
        if (script.parentNode) {
          document.head.removeChild(script);
        }
        injectedElements.current.delete(script.innerHTML);
      });
      appendedNoscripts.forEach(noscript => {
        if (noscript.parentNode) {
          document.body.removeChild(noscript);
        }
        injectedElements.current.delete(noscript.innerHTML);
      });
      // Remove dynamically added scripts
      dynamicallyAddedScripts.current.forEach(script => {
        if (script.parentNode) {
          document.head.removeChild(script);
        }
        injectedElements.current.delete(script.src || script.innerHTML);
      });

      // Disconnect the observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [scriptsRawCode]);
};

export default useTrackingScripts;
